import type { PermissionType } from '@/graphql/generated/types.generated';
import { PermissionType as PermissionTypeEnum } from '@/graphql/generated/types.generated';
import type { PlanPermissionPricesQuery } from '@/graphql/generated/plan-permission-prices.generated';
import type { PlansQuery } from '@/graphql/generated/plans.generated';

// ─── Normalised feature shape after mapping from API data ────────────────────

export interface MappedFeature {
    id: string; // permission id
    code: string; // permission code (kebab-case)
    label: string; // display label (after display config override)
    description: string; // i18n key for the permission description
    type: PermissionType; // from codegen
    value: number; // base value for the selected plan (transformed if applicable)
    displayValue: string; // formatted string e.g. "10 MB" or "500"
    // ── Numeric add-on ──
    addOnPricePerUnit?: number;
    addOnTiers?: { upTo?: number | null; price: number }[];
    addOnStep?: number;
    addOnMax?: number;
    // ── Boolean add-on ──
    addOnPrice?: number;
    // ── Enum add-on ──
    addOnOptions?: { id: string; value: string; label: string; price: number }[];
    // ── Dependency ──
    dependsOn?: string; // code of the parent permission that must be active
    dependsOnIncludedInPlan?: boolean; // parent permission is granted by the selected plan
}

// ─── Normalised plan shape after mapping from API data ───────────────────────

export interface MappedPlan {
    id: string;
    code: string;
    name: string;
    isTrial: boolean;
    mostPopular: boolean;
    monthlyPrice: number; // from prices[interval=month].monthlyPrice
    yearlyMonthlyPrice: number; // from prices[interval=year].monthlyPrice
    currency: { symbol: string } | null; // from prices[].currency
    features: MappedFeature[];
}

type PermissionPriceItem = NonNullable<PlanPermissionPricesQuery['planPermissionPrices'][number]['price']>;

type PermissionItem = PlanPermissionPricesQuery['planPermissionPrices'][number];

// ─── Price extraction helpers ─────────────────────────────────────────────────

function extractAddOnPricing(
    price: PermissionPriceItem | null | undefined,
    type: PermissionType,
    increments: number,
): Partial<MappedFeature> {
    if (!price) return {};

    if (price.flat) {
        if (type === PermissionTypeEnum.Boolean) {
            return { addOnPrice: price.flat.monthlyPrice };
        }
        return {
            addOnPricePerUnit: price.flat.monthlyPrice,
            addOnStep: increments,
        };
    }

    if (price.tier && price.tier.length > 0) {
        return {
            addOnTiers: price.tier.map((t) => ({
                upTo: t.upTo,
                price: t.monthlyPrice,
            })),
            addOnStep: increments,
        };
    }

    return {};
}

// ─── Permission → MappedFeature ─────────────────────────────────────────────

function mapPermissionToFeature(
    perm: PermissionItem,
    planValue: number,
    valueByCode: Map<string, number>,
): MappedFeature {
    const label = perm.nameKey;
    const price = perm.price ?? null;
    const value = planValue;

    const addOnPricing = extractAddOnPricing(price, perm.type, perm.increments);

    // ENUM permissions → selector options (always monthly prices; discount applied at total)
    let addOnOptions: MappedFeature['addOnOptions'] | undefined;

    if (perm.enumPermission && perm.enumPermission.length > 0) {
        // Enum tiers are a ladder: the API sends each tier's absolute position on it, and the
        // upgrade cost is the distance from whatever tier this plan already grants. So a plan
        // on the bottom rung sees 5 / 10 / 15… going up, while a plan already partway up only
        // pays for the rungs it gains. Mirrors `enumUpgradePrice` on the backend, which
        // revalidates the total at checkout.
        const includedPrice =
            perm.enumPermission.find((gp) => valueByCode.has(gp.code))?.price?.flat?.monthlyPrice ?? 0;

        addOnOptions = perm.enumPermission.map((gp) => ({
            id: gp.id,
            value: gp.code,
            label: gp.nameKey,
            price: Math.max(0, (gp.price?.flat?.monthlyPrice ?? 0) - includedPrice),
        }));
    }

    // For enum type, the included option is whichever child has its code present in the
    // plan's permissions map (the plan sends children directly, not the parent).
    // displayValue holds the child's code — a stable identifier, not a label.
    // If no child is found in the plan, displayValue is empty (not included).
    // For other types, displayValue is the raw numeric value with optional unit suffix.
    const displayValue =
        perm.type === PermissionTypeEnum.Enum && addOnOptions?.length
            ? (perm.enumPermission?.find((gp) => valueByCode.has(gp.code))?.code ?? '')
            : String(value);

    const resolvedMax = perm.max != null ? perm.max : undefined;

    // A base-plan-only parent (e.g. `dashboards`) is filtered out of the purchasable add-on list,
    // so its active state can't be read from the add-on features — resolve it here from the plan's
    // own granted permissions instead.
    const dependsOnIncludedInPlan = perm.dependOn?.code
        ? (valueByCode.get(perm.dependOn.code) ?? 0) > 0
        : true;

    return {
        id: perm.id,
        code: perm.code,
        label,
        description: perm.descriptionKey,
        type: perm.type,
        value,
        displayValue,
        ...addOnPricing,
        ...(resolvedMax !== undefined ? { addOnMax: resolvedMax } : {}),
        ...(addOnOptions ? { addOnOptions } : {}),
        ...(perm.dependOn?.code ? { dependsOn: perm.dependOn.code, dependsOnIncludedInPlan } : {}),
    };
}

// ─── Main mapping function ────────────────────────────────────────────────────

export interface MappedCalculatorData {
    plans: MappedPlan[];
}

export function mapGraphqlToCalculator(
    permissionsData: PlanPermissionPricesQuery['planPermissionPrices'],
    plansData: PlansQuery['plans'],
): MappedCalculatorData {
    const plans: MappedPlan[] = plansData
        .slice()
        .sort((a, b) => a.order - b.order)
        .map((planPreview) => {
            // Build a lookup map from permission code → value for this plan
            const valueByCode = new Map<string, number>(
                planPreview.permissions.map((pp) => [pp.permission.code, pp.value]),
            );

            const features: MappedFeature[] = permissionsData.map((perm) => {
                const rawValue = valueByCode.get(perm.code) ?? 0;
                return mapPermissionToFeature(perm, rawValue, valueByCode);
            });

            const monthPrice = planPreview.prices.find((p) => p.interval === 'month');
            const yearPrice = planPreview.prices.find((p) => p.interval === 'year') ?? monthPrice;

            return {
                id: planPreview.plan.id,
                code: planPreview.code,
                name: planPreview.plan.name,
                isTrial: planPreview.plan.isTrial,
                mostPopular: planPreview.mostPopular,
                monthlyPrice: monthPrice?.monthlyPrice ?? 0,
                yearlyMonthlyPrice: yearPrice?.monthlyPrice ?? monthPrice?.monthlyPrice ?? 0,
                currency: monthPrice?.currency ?? yearPrice?.currency ?? null,
                features,
            };
        });

    return { plans };
}
